import { useEffect, useRef } from "react";
import "./messaging-body.css";
import { util } from "../helpers/common";
import { CONVERSATION_CONSTANTS } from "../helpers/constants";
import { useStrings } from '../i18n/useStrings';

// Import children components to plug in and render.
import ConversationEntry from "./ConversationEntry";
import TypingIndicator from "./typingIndicator";

export default function MessagingBody({ conversationEntries, conversationStatus, typingParticipants, showTypingIndicator }) {
    const strings = useStrings();

    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [conversationEntries, showTypingIndicator, typingParticipants]);

    /**
     * Builds a list of conversation entries where each conversation-entry represents an object of type defined in constants#CONVERSATION_CONSTANTS.EntryTypes.
     * @returns {string}
     */
    const conversationEntriesListView = conversationEntries.map(conversationEntry =>
        <ConversationEntry
            key={conversationEntry.messageId} 
            conversationEntry={conversationEntry} />
    );

    /**
     * Generates a text with conversation start date and time.
     * @returns {string}
     */
    function generateConversationStartTimeText() {
        if (conversationEntries.length) {
            const conversationStartTimestamp = conversationEntries[0].transcriptedTimestamp;
            const startDate = util.getFormattedDate(conversationStartTimestamp);
            const startTime = util.getFormattedTime(conversationStartTimestamp);
            const conversationStartTimeText = `${strings.conversationStarted} ${startDate} ${strings.at} ${startTime}`;
            return conversationStartTimeText;
        }
        return "";
    }

    /**
     * Generates a text with conversation end date and time.
     * @returns {string}
     */
    function generateConversationEndTimeText() {
        const conversationEndTimestamp = Date.now();
        const endDate = util.getFormattedDate(conversationEndTimestamp);
        const endTime = util.getFormattedTime(conversationEndTimestamp);
        const conversationEndTimeText = `${strings.conversationEnded} ${endDate} ${strings.at} ${endTime}`;

        return conversationEndTimeText;
    }

    return (
        <div className="messagingBody">
            {conversationEntries.length > 0 && <p className="conversationStartTimeText">{generateConversationStartTimeText()}</p>}
            <ul className="conversationEntriesListView">
                {conversationEntriesListView}
                <div ref={messagesEndRef} />
            </ul>
            {showTypingIndicator && <TypingIndicator typingParticipants={typingParticipants}/>}
            {conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION && <p className="conversationEndTimeText">{generateConversationEndTimeText()}</p>}
        </div>
    );
}